/**
 * Language Switcher Component
 * Allows users to switch between available languages
 */

import { useTranslation } from 'react-i18next';
import { getAvailableLanguages, changeLanguage } from '../lib/i18n';
import { useAuth } from '../contexts/AuthContext';
import { apiClient } from '../lib/api-client';

interface LanguageSwitcherProps {
  /** Display style: 'dropdown' for select, 'buttons' for button group */
  variant?: 'dropdown' | 'buttons';
  /** Additional CSS classes */
  className?: string;
  /** Show native language names (日本語) or English names (Japanese) */
  showNativeName?: boolean;
}

export default function LanguageSwitcher({
  variant = 'dropdown',
  className = '',
  showNativeName = true,
}: LanguageSwitcherProps) {
  const { i18n } = useTranslation();
  const { user, isAuthenticated } = useAuth();
  const languages = getAvailableLanguages();
  const currentLang = i18n.language;

  const handleChange = async (lang: string) => {
    console.log('[LanguageSwitcher] Changing language to:', lang);
    console.log('[LanguageSwitcher] isAuthenticated:', isAuthenticated);
    console.log('[LanguageSwitcher] user?.role:', user?.role);
    await changeLanguage(lang);
    // Save to backend if logged in as viewer (role can be 'VIEWER' or 'USER')
    if (isAuthenticated && (user?.role === 'VIEWER' || user?.role === 'USER')) {
      console.log('[LanguageSwitcher] Calling API to save language');
      try {
        await apiClient.updateLanguage(lang);
        console.log('[LanguageSwitcher] Language saved successfully');
      } catch (err) {
        console.error('Failed to save language preference to server:', err);
      }
    } else {
      console.log('[LanguageSwitcher] Skipping API call - not authenticated or not viewer/user');
    }
  };

  if (variant === 'buttons') {
    return (
      <div className={`flex gap-1 ${className}`}>
        {languages.map((lang) => (
          <button
            key={lang.code}
            onClick={() => handleChange(lang.code)}
            className={`px-3 py-1 text-sm rounded transition-colors ${
              currentLang === lang.code
                ? 'bg-primary text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            {showNativeName ? lang.nativeName : lang.name}
          </button>
        ))}
      </div>
    );
  }

  // Default: dropdown
  return (
    <select
      value={currentLang}
      onChange={(e) => handleChange(e.target.value)}
      className={`px-3 py-1 text-sm border border-gray-300 rounded bg-white text-gray-700 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent ${className}`}
    >
      {languages.map((lang) => (
        <option key={lang.code} value={lang.code}>
          {showNativeName ? lang.nativeName : lang.name}
        </option>
      ))}
    </select>
  );
}
